import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Shield, 
  Plus, 
  Trash2, 
  AlertTriangle, 
  CheckCircle,
  X,
  Info
} from 'lucide-react';

interface IPBlockingInterfaceProps {
  userId: number;
  onBlockedIPsUpdate?: (blockedIPs: string[]) => void;
}

export const IPBlockingInterface: React.FC<IPBlockingInterfaceProps> = ({
  userId,
  onBlockedIPsUpdate
}) => {
  const [blockedIPs, setBlockedIPs] = useState<string[]>([]);
  const [newIP, setNewIP] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchBlockedIPs();
  }, [userId]);

  const fetchBlockedIPs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/users/${userId}/blocked-ips`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blocked IPs');
      }

      const data = await response.json();
      if (data.success) {
        setBlockedIPs(data.data || []);
        onBlockedIPsUpdate?.(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch blocked IPs');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch blocked IPs');
    } finally {
      setLoading(false);
    }
  };

  const addBlockedIP = async () => {
    if (!newIP.trim()) {
      setError('Please enter an IP address');
      return;
    }

    if (!isValidIP(newIP.trim())) {
      setError('Please enter a valid IP address');
      return;
    }

    if (blockedIPs.includes(newIP.trim())) {
      setError('This IP address is already blocked');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`/api/users/${userId}/blocked-ips`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ ipAddress: newIP.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to add blocked IP');
      }

      const data = await response.json();
      if (data.success) {
        const updatedIPs = [...blockedIPs, newIP.trim()];
        setBlockedIPs(updatedIPs);
        setNewIP('');
        setSuccess(`IP address ${newIP.trim()} has been blocked`);
        onBlockedIPsUpdate?.(updatedIPs);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to add blocked IP');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to add blocked IP');
    } finally {
      setSaving(false);
    }
  };

  const removeBlockedIP = async (ipToRemove: string) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const response = await fetch(`/api/users/${userId}/blocked-ips`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ ipAddress: ipToRemove })
      });

      if (!response.ok) {
        throw new Error('Failed to remove blocked IP');
      }

      const data = await response.json();
      if (data.success) {
        const updatedIPs = blockedIPs.filter(ip => ip !== ipToRemove);
        setBlockedIPs(updatedIPs);
        setSuccess(`IP address ${ipToRemove} has been unblocked`);
        onBlockedIPsUpdate?.(updatedIPs);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to remove blocked IP');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to remove blocked IP');
    } finally {
      setSaving(false);
    }
  };

  const isValidIP = (ip: string): boolean => {
    // Basic IP validation (IPv4)
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      addBlockedIP();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading IP blocking settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            IP Address Blocking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Blocked IP addresses will not be tracked in your analytics and will not contribute to your pricing optimization data. 
                This is useful for filtering out internal traffic, bots, or unwanted visitors.
              </AlertDescription>
            </Alert>

            {/* Add IP Form */}
            <div className="flex gap-2">
              <Input
                placeholder="Enter IP address (e.g., 192.168.1.1)"
                value={newIP}
                onChange={(e) => setNewIP(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button 
                onClick={addBlockedIP} 
                disabled={saving || !newIP.trim()}
                className="px-6"
              >
                <Plus className="h-4 w-4 mr-2" />
                Block IP
              </Button>
            </div>

            {/* Status Messages */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Blocked IPs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Blocked IP Addresses</span>
            <Badge variant="secondary">
              {blockedIPs.length} blocked
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {blockedIPs.length === 0 ? (
            <div className="text-center py-8">
              <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No IP addresses are currently blocked</p>
              <p className="text-sm text-muted-foreground mt-2">
                Add IP addresses above to start blocking them from your analytics
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {blockedIPs.map((ip, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono text-sm">{ip}</span>
                    <Badge variant="outline" className="text-xs">
                      Blocked
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBlockedIP(ip)}
                    disabled={saving}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Blocking Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">{blockedIPs.length}</div>
              <div className="text-sm text-muted-foreground">Blocked IPs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {blockedIPs.length > 0 ? 'Active' : 'Inactive'}
              </div>
              <div className="text-sm text-muted-foreground">Blocking Status</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {blockedIPs.length > 0 ? 'Protected' : 'Open'}
              </div>
              <div className="text-sm text-muted-foreground">Analytics Status</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
